<template>
  <div class="header-mobile-search">
    <SButtonIcon
      ref="buttonRef"
      action-type="tertiary-soft"
      :aria-expanded="isOpen"
      :aria-label="tr('main_site.layout.header_search_placeholder')"
      icon="fa-regular fa-magnifying-glass"
      @click="toggleSearch"
    />

    <Teleport to="body">
      <Transition name="header-mobile-search__panel">
        <div v-if="isOpen" ref="panelRef" class="header-mobile-search__panel">
          <SButtonSearch
            always-open
            :aria-label="tr('main_site.layout.header_search_placeholder')"
            fluid
            :placeholder="tr('main_site.layout.header_search_placeholder')"
            @search="onSearch"
          />
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside, useMediaQuery } from "@vueuse/core";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";

import { SButtonIcon, SButtonSearch } from "@solvari/common-fe";
import { ziggyUnsafe } from "@solvari/common-fe/integrations";
import { useI18nMainSiteLayout } from "@solvari/translations";

const { tr } = useI18nMainSiteLayout();

const isOpen = ref(false);
const isDesktop = useMediaQuery("(min-width: 1000px)");

const buttonRef = useTemplateRef<InstanceType<typeof SButtonIcon>>("buttonRef");
const panelRef = useTemplateRef<HTMLElement>("panelRef");

const buttonEl = computed(
  () => buttonRef.value?.$el as HTMLElement | undefined,
);

onClickOutside(
  panelRef,
  () => {
    isOpen.value = false;
  },
  {
    ignore: [buttonEl],
    capture: true,
  },
);

watch(isDesktop, (desktop) => {
  if (desktop) {
    isOpen.value = false;
  }
});

async function toggleSearch() {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    await nextTick();
    panelRef.value?.querySelector("input")?.focus();
  }
}

function onSearch(term: string) {
  window.location.href = `${ziggyUnsafe("client.search.base")}?term=${encodeURIComponent(term)}`;
}
</script>

<style lang="postcss">
.header-mobile-search {
  @screen 1000 {
    @apply hidden;
  }

  &__panel {
    @apply fixed left-0 right-0 top-[88px] z-[49] bg-white px-4 py-3 shadow-md; /* top = HeaderContact (32px) + SHeaderBase h-14 (56px) */

    @screen 500 {
      @apply top-24; /* top = HeaderContact (32px) + SHeaderBase h-16 (64px) */
    }

    &-enter-active,
    &-leave-active {
      @apply transition-all duration-200;
    }

    &-enter-from,
    &-leave-to {
      @apply -translate-y-2 opacity-0;
    }
  }
}
</style>
